import { useState } from 'react';
import { Box, Typography, Button, alpha, CircularProgress, Snackbar, Alert } from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import { PageLayout, PageSection, GlassCard } from '@paneon/ui';
import { useTheme } from '@paneon/theme';
import { usePets } from '../hooks/usePets';
import { PetCard } from '../components/PetCard';
import { AddPetDialog, type PetFormData } from '../components/AddPetDialog';
import type { Pet } from '../services/api';

export function PetsPage() {
  const { isDarkMode } = useTheme();
  const { pets, loading, error, addPet, updatePet, deletePet } = usePets();

  const [dialogOpen, setDialogOpen] = useState(false);
  const [editPet, setEditPet] = useState<Pet | null>(null);
  const [snackbar, setSnackbar] = useState<{
    open: boolean;
    message: string;
    severity: 'success' | 'error';
  }>({ open: false, message: '', severity: 'success' });

  const handleAdd = () => {
    setEditPet(null);
    setDialogOpen(true);
  };

  const handleEdit = (pet: Pet) => {
    setEditPet(pet);
    setDialogOpen(true);
  };

  const handleDelete = async (pet: Pet) => {
    if (window.confirm(`Möchtest du "${pet.name}" wirklich löschen?`)) {
      try {
        await deletePet(pet.id);
        setSnackbar({
          open: true,
          message: `${pet.name} wurde gelöscht`,
          severity: 'success',
        });
      } catch (err) {
        setSnackbar({
          open: true,
          message: 'Fehler beim Löschen',
          severity: 'error',
        });
      }
    }
  };

  const handleSave = async (data: PetFormData) => {
    try {
      if (editPet) {
        await updatePet(editPet.id, data);
        setSnackbar({
          open: true,
          message: `${data.name} wurde aktualisiert`,
          severity: 'success',
        });
      } else {
        await addPet(data);
        setSnackbar({
          open: true,
          message: `${data.name} wurde hinzugefügt`,
          severity: 'success',
        });
      }
    } catch (err) {
      setSnackbar({
        open: true,
        message: 'Fehler beim Speichern',
        severity: 'error',
      });
      throw err;
    }
  };

  // Loading state
  if (loading) {
    return (
      <PageLayout title="Haustiere">
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            minHeight: 200,
          }}
        >
          <CircularProgress />
        </Box>
      </PageLayout>
    );
  }

  // Error state
  if (error) {
    return (
      <PageLayout title="Haustiere">
        <PageSection>
          <GlassCard sx={{ p: 3 }}>
            <Typography color="error">{error}</Typography>
          </GlassCard>
        </PageSection>
      </PageLayout>
    );
  }

  return (
    <>
      <PageLayout
        title="Haustiere"
        rightAction={
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            size="small"
            sx={{ borderRadius: 2, textTransform: 'none' }}
            onClick={handleAdd}
          >
            Hinzufügen
          </Button>
        }
      >
        {pets.length === 0 ? (
          <PageSection>
            <GlassCard
              sx={{
                p: 4,
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                minHeight: 200,
              }}
            >
              <Box sx={{ fontSize: 48, mb: 2 }}>🐾</Box>
              <Typography
                variant="h6"
                sx={{
                  color: isDarkMode ? '#fff' : '#000',
                  mb: 1,
                }}
              >
                Noch keine Haustiere
              </Typography>
              <Typography
                variant="body2"
                sx={{
                  color: isDarkMode ? alpha('#fff', 0.6) : alpha('#000', 0.6),
                  mb: 2,
                  textAlign: 'center',
                }}
              >
                Füge dein erstes Haustier hinzu, um mit dem Tracking zu beginnen
              </Typography>
              <Button
                variant="outlined"
                startIcon={<AddIcon />}
                sx={{ borderRadius: 2, textTransform: 'none' }}
                onClick={handleAdd}
              >
                Haustier hinzufügen
              </Button>
            </GlassCard>
          </PageSection>
        ) : (
          <PageSection>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              {pets.map(pet => (
                <PetCard key={pet.id} pet={pet} onEdit={handleEdit} onDelete={handleDelete} />
              ))}
            </Box>
          </PageSection>
        )}
      </PageLayout>

      <AddPetDialog
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
        onSave={handleSave}
        editPet={editPet}
      />

      <Snackbar
        open={snackbar.open}
        autoHideDuration={3000}
        onClose={() => setSnackbar(s => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          severity={snackbar.severity}
          onClose={() => setSnackbar(s => ({ ...s, open: false }))}
          sx={{ borderRadius: 2 }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </>
  );
}
